// src/Components/Forms/Financement/Penalite/DemandePenalite.tsx

import { useState, useEffect, useMemo, useCallback } from 'react';
import { Tabs, Space, Button, Form, Spin, Tag, Input, Card, InputNumber, Statistic, Tooltip, Divider } from 'antd';
import Table from '../../../../TableResizable';
import type { TabsProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  UserOutlined,
  ReloadOutlined,
  CheckOutlined,
  EyeOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { Box, Typography, Grid, Paper } from '@mui/material';

import Tab1 from './Tab1';
import Utilisateur from '../../../../../Models/Utilisateur';
import { useAuthHeader, useAuthUser } from '../../../../../Services/Auth/sessionAuth';
import Data from '../../../../../Data/Data.json';
import SweetAlert from 'sweetalert2';
import { Crypter } from '../../../../../Functions/Security/Crypter';
import { PenaliteDto } from '../../../../../Models/PenaliteDto';
import Params from '../../../../../Types/ParamsPicklist';

type PenaliteRow = PenaliteDto & {
  key: React.Key;
};

interface PenaliteGroup {
  key: string;
  NumeroFinancement: string;
  CodeFinancement: string;
  Numcli?: string;
  Nomcli?: string;
  children: PenaliteRow[];
  totalPenalites: number;
  montantTotal: number;
  montantImputeTotal: number;
}

type MontantsImputesMap = Record<string, number>;

const DemandePenalite = () => {
  const auth = useAuthUser();
  const user: Utilisateur = JSON.parse(JSON.stringify(auth()));
  const authHeader = useAuthHeader();

  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [penalites, setPenalites] = useState<PenaliteDto[]>([]);
  const [filteredData, setFilteredData] = useState<PenaliteDto[]>([]);
  const [groupedData, setGroupedData] = useState<PenaliteGroup[]>([]);

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedPenalite, setSelectedPenalite] = useState<PenaliteDto | null>(null);
  const [detailData, setDetailData] = useState<any>(null);

  const [activeTab, setActiveTab] = useState('1');
  const [searchText, setSearchText] = useState('');

  // Montants imputés
  const [montantsImputes, setMontantsImputes] = useState<MontantsImputesMap>({});

  const getPenaliteKey = useCallback((penalite: PenaliteDto): string => {
    return String(penalite.PenaliteID ?? '');
  }, []);

  const getMontantConstate = useCallback((penalite: PenaliteDto): number => {
    return Number(penalite.MontantPenalite ?? 0);
  }, []);

  const getMontantImpute = useCallback(
    (penalite: PenaliteDto): number => {
      const key = getPenaliteKey(penalite);
      if (Object.prototype.hasOwnProperty.call(montantsImputes, key)) {
        return Number(montantsImputes[key] ?? 0);
      }
      return getMontantConstate(penalite);
    },
    [getMontantConstate, getPenaliteKey, montantsImputes],
  );

  // Suppression des virgules et des décimales, conservation des espacements de milliers, suppression de XAF
  const formatMontant = useCallback((value: number): string => {
    return Number(value ?? 0).toLocaleString('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }, []);

  // Chargement initial : filtrage sur Statut = CONSTATEE
  const loadPenalites = useCallback(() => {
    setLoading(true);

    const searchParams: Params = {
      CodeAgence: '1',
      Table: 'ViewAnyPenaliteDetail',
      Colonne1: 'PenaliteID',
      Colonne2: 'PenaliteID',
      Init: '',
      Colagence: '1',
      Choix: 37,
      colfiltre: 'Statut',
      valfiltre: 'CONSTATEE',
    };

    const config = {
      method: 'get',
      maxBodyLength: Infinity,
      url: Data.ApiUrl.AnySearch,
      headers: {
        'Content-Type': 'application/json',
        Authorization: authHeader(),
        CypherSearch: Crypter.encrypt(JSON.stringify(searchParams)),
      },
    };

    axios
      .request(config)
      .then((response) => {
        setLoading(false);
        if (!response.data) return;

        const res = JSON.parse(Crypter.decrypt(response.data)).Value;

        if (res.StatutCode === 200) {
          const datas: PenaliteDto[] = res.Result?.Table || [];
          setPenalites(datas);
          setFilteredData(datas);

          // Initialisation des montants imputés
          setMontantsImputes((prev) => {
            const next = { ...prev };
            datas.forEach((p) => {
              const key = String(p.PenaliteID ?? '');
              if (key && !(key in next)) {
                next[key] = Number(p.MontantPenalite ?? 0);
              }
            });
            return next;
          });
        } else {
          SweetAlert.fire({ title: res.Message, icon: 'warning' });
        }
      })
      .catch((error) => {
        setLoading(false);
        SweetAlert.fire({ title: 'Erreur', text: error?.message || String(error), icon: 'error' });
      });
  }, [authHeader]);

  useEffect(() => {
    loadPenalites();
  }, [loadPenalites]);

  // Filtre de recherche
  useEffect(() => {
    let data = penalites;
    if (searchText.trim()) {
      const lower = searchText.toLowerCase().trim();
      data = data.filter(
        (p) =>
          p.NumeroFinancement?.toLowerCase().includes(lower) ||
          p.CodeFinancement?.toLowerCase().includes(lower) ||
          p.Numcli?.toLowerCase().includes(lower) ||
          p.Nomcli?.toLowerCase().includes(lower) ||
          p.Statut?.toLowerCase().includes(lower) ||
          p.PenaliteID?.toString().includes(lower) ||
          p.NumeroEcheance?.toString().includes(lower),
      );
    }
    setFilteredData(data);
  }, [searchText, penalites]);

  // Gestion des montants imputés
  const handleMontantImputeChange = useCallback(
    (penalite: PenaliteDto, value: number | null) => {
      const key = getPenaliteKey(penalite);
      if (!key) return;

      const constate = getMontantConstate(penalite);
      let impute = Number(value ?? 0);
      if (impute < 0) impute = 0;
      if (impute > constate) impute = constate;

      setMontantsImputes((prev) => ({ ...prev, [key]: Math.floor(impute) })); // On s'assure d'avoir un entier
    },
    [getMontantConstate, getPenaliteKey],
  );

  const handleResetMontantImpute = useCallback(
    (penalite: PenaliteDto) => {
      const key = getPenaliteKey(penalite);
      if (!key) return;
      setMontantsImputes((prev) => ({ ...prev, [key]: Math.floor(getMontantConstate(penalite)) }));
    },
    [getMontantConstate, getPenaliteKey],
  );

  const isMontantModifie = useCallback(
    (penalite: PenaliteDto): boolean => {
      return getMontantConstate(penalite) !== getMontantImpute(penalite);
    },
    [getMontantConstate, getMontantImpute],
  );

  // Regroupement
  useEffect(() => {
    const groups: Record<string, PenaliteRow[]> = {};
    filteredData.forEach((p) => {
      const key = p.NumeroFinancement || 'SANS_FINANCEMENT';
      if (!groups[key]) groups[key] = [];
      groups[key].push({ ...p, key: p.PenaliteID ?? `${key}_${groups[key].length}` });
    });

    const grouped: PenaliteGroup[] = Object.keys(groups).map((key) => {
      const items = groups[key];
      const first = items[0];
      return {
        key: `grp_${key}`,
        NumeroFinancement: key,
        CodeFinancement: first?.CodeFinancement || '',
        Numcli: first?.Numcli || '',
        Nomcli: first?.Nomcli || '',
        children: items,
        totalPenalites: items.length,
        montantTotal: items.reduce((sum, p) => sum + getMontantConstate(p), 0),
        montantImputeTotal: items.reduce((sum, p) => sum + getMontantImpute(p), 0),
      };
    });

    setGroupedData(grouped);
  }, [filteredData, getMontantConstate, getMontantImpute]);

  // Totaux globaux
  const totals = useMemo(() => {
    const constate = filteredData.reduce((sum, p) => sum + getMontantConstate(p), 0);
    const impute = filteredData.reduce((sum, p) => sum + getMontantImpute(p), 0);
    return {
      nombre: filteredData.length,
      montantConstate: constate,
      montantImpute: impute,
      ecart: constate - impute,
    };
  }, [filteredData, getMontantConstate, getMontantImpute]);

  // Pénalités sélectionnées
  const selectedPenalites = useMemo(() => {
    const ids = new Set(
      selectedRowKeys
        .map(String)
        .filter((key) => !key.startsWith('grp_')),
    );
    return penalites.filter((p) => ids.has(String(p.PenaliteID)));
  }, [penalites, selectedRowKeys]);

  const totalsSelection = useMemo(() => {
    let constate = 0,
      impute = 0;
    selectedPenalites.forEach((p) => {
      constate += getMontantConstate(p);
      impute += getMontantImpute(p);
    });
    return { constate, impute };
  }, [selectedPenalites, getMontantConstate, getMontantImpute]);

  const validateSelectedMontants = useCallback(() => {
    for (const p of selectedPenalites) {
      const impute = getMontantImpute(p);
      if (impute < 0 || impute > getMontantConstate(p)) {
        return {
          valid: false,
          message: `Pénalité #${p.PenaliteID} : montant imputé ${impute} hors limites.`,
        };
      }
    }
    return { valid: true, message: '' };
  }, [selectedPenalites, getMontantConstate, getMontantImpute]);

  // Demande d'imputation
  const handleDemande = () => {
    if (selectedPenalites.length === 0) {
      SweetAlert.fire({ title: 'Aucune sélection', icon: 'warning' });
      return;
    }

    const validation = validateSelectedMontants();
    if (!validation.valid) {
      SweetAlert.fire({ title: 'Montant invalide', text: validation.message, icon: 'warning' });
      return;
    }

    const payload = {
      Penalites: selectedPenalites.map((p) => ({
        PenaliteID: p.PenaliteID!,
        MontantAImputer: getMontantImpute(p),
      })),
      CODUTI: user.coduti,
      CodAge: user.codage,
    };

    SweetAlert.fire({
      title: 'Confirmer la demande d\'imputation',
      html: `
        <div style="text-align:left">
          <p><strong>${selectedPenalites.length}</strong> pénalité(s)</p>
          <p>Montant total imputé : <strong>${formatMontant(
            selectedPenalites.reduce((sum, p) => sum + getMontantImpute(p), 0)
          )}</strong></p>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Confirmer',
      cancelButtonText: 'Annuler',
      icon: 'question',
    }).then((res) => {
      if (!res.isConfirmed) return;

      setLoading(true);
      const encrypted = Crypter.encrypt(JSON.stringify(payload));
      const config = {
        method: 'post',
        url: Data.ApiUrl.CreatePenalite,
        headers: {
          Authorization: authHeader(),
          'Content-Type': 'application/json',
        },
        data: encrypted,
      };

      axios
        .request(config)
        .then((response) => {
          setLoading(false);
          if (!response.data) return;
          const res = JSON.parse(Crypter.decrypt(response.data)).Value;
          if (res.StatutCode === 200) {
            SweetAlert.fire({ title: res.Message, text: res.Result, icon: 'success' });
            loadPenalites();
            setSelectedRowKeys([]);
          } else {
            SweetAlert.fire({ title: res.Message || 'Erreur', text: res.Result, icon: 'warning' });
          }
        })
        .catch((error) => {
          setLoading(false);
          SweetAlert.fire({ title: 'Erreur', text: error?.message || String(error), icon: 'error' });
        });
    });
  };

  // Colonnes
  const columns: ColumnsType<PenaliteGroup | PenaliteRow> = [
    {
      title: 'Pénalité',
      dataIndex: 'PenaliteID',
      key: 'PenaliteID',
      width: 100,
      align: 'center',
      render: (text, record) => {
        if ('children' in record && Array.isArray(record.children)) {
          return <Tag color="blue">{record.totalPenalites} pénalité(s)</Tag>;
        }
        return text;
      },
    },
    {
      title: 'Type financement',
      dataIndex: 'CodeFinancement',
      key: 'CodeFinancement',
      width: 140,
      render: (text, record) => {
        if ('children' in record && Array.isArray(record.children)) {
          return <strong>{record.CodeFinancement || '-'}</strong>;
        }
        return text || '-';
      },
    },
    {
      title: 'N° Financement',
      dataIndex: 'NumeroFinancement',
      key: 'NumeroFinancement',
      width: 160,
    },
    {
      title: 'Client',
      dataIndex: 'Nomcli',
      key: 'Nomcli',
      render: (text, record) => {
        if ('children' in record && Array.isArray(record.children)) {
          return <strong>{text || record.Numcli || '-'}</strong>;
        }
        return text || record.Numcli || '-';
      },
    },
    {
      title: 'N° Échéance',
      dataIndex: 'NumeroEcheance',
      key: 'NumeroEcheance',
      width: 110,
      align: 'center',
      render: (text, record) => {
        if ('children' in record && Array.isArray(record.children)) {
          return <Tag color="cyan">{record.totalPenalites} échéance(s)</Tag>;
        }
        return text || '-';
      },
    },
    {
      title: 'Date constat',
      dataIndex: 'DateConstat',
      key: 'DateConstat',
      width: 120,
      render: (text, record) => {
        if ('children' in record && Array.isArray(record.children)) return '';
        return text ? new Date(text).toLocaleDateString('fr-FR') : '';
      },
    },
    {
      title: 'Jours retard',
      dataIndex: 'JoursRetard',
      key: 'JoursRetard',
      width: 100,
      align: 'center',
      render: (text, record) => {
        if ('children' in record && Array.isArray(record.children)) return '';
        return text ?? '-';
      },
    },
    {
      title: 'Montant constaté',
      dataIndex: 'MontantPenalite',
      key: 'MontantPenalite',
      width: 160,
      align: 'right',
      render: (text, record) => {
        if ('children' in record && Array.isArray(record.children)) {
          return (
            <Tooltip title="Total constaté en base">
              <strong>{formatMontant(record.montantTotal)}</strong>
            </Tooltip>
          );
        }
        return <Typography component="span" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{formatMontant(Number(text ?? 0))}</Typography>;
      },
    },
    {
      title: 'Montant imputé',
      key: 'MontantImpute',
      width: 190,
      align: 'right',
      render: (_, record) => {
        if ('children' in record && Array.isArray(record.children)) {
          const diff = record.montantTotal - record.montantImputeTotal;
          return (
            <div>
              <Tooltip title="Total imputé">
                <strong>{formatMontant(record.montantImputeTotal)}</strong>
              </Tooltip>
              {diff !== 0 && (
                <div style={{ marginTop: 4, fontSize: 11, color: '#d48806' }}>
                  Écart : {formatMontant(diff)}
                </div>
              )}
            </div>
          );
        }
        const penalite = record as PenaliteRow;
        const constate = getMontantConstate(penalite);
        const impute = getMontantImpute(penalite);
        const modifie = isMontantModifie(penalite);

        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
            <InputNumber
              value={impute}
              min={0}
              max={constate}
              precision={0} // Suppression des décimales/virgules dans l'input
              controls
              size="small"
              style={{ width: 150, borderColor: modifie ? '#faad14' : undefined }}
              formatter={(v) => (v === undefined || v === null ? '' : `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' '))}
              parser={(v) => Number(String(v ?? '').replace(/\s/g, ''))} // Le parser ignore juste les espaces
              onChange={(v) => handleMontantImputeChange(penalite, v)}
            />
            {modifie && (
              <Tooltip title="Rétablir le montant constaté">
                <Button
                  type="link"
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={() => handleResetMontantImpute(penalite)}
                >
                  Réinitialiser
                </Button>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: 'Statut',
      dataIndex: 'Statut',
      key: 'Statut',
      width: 120,
      align: 'center',
      render: (text, record) => {
        if ('children' in record && Array.isArray(record.children)) return '';
        return (
          <Tag color={text === 'CONSTATEE' ? 'blue' : text === 'ENCAISSE' ? 'green' : text === 'ANNULE' ? 'red' : 'default'}>
            {text || 'N/A'}
          </Tag>
        );
      },
    },
    {
      title: 'Action',
      key: 'action',
      width: 110,
      fixed: 'right',
      align: 'center',
      render: (_, record) => {
        if ('children' in record && Array.isArray(record.children)) return null;
        const penalite = record as PenaliteRow;
        return (
          <Button size="small" icon={<EyeOutlined />} onClick={() => handleSelectPenalite(penalite)}>
            Détails
          </Button>
        );
      },
    },
  ];

  // Sélection d'une pénalité pour les détails
  const handleSelectPenalite = (penalite: PenaliteDto) => {
    setSelectedPenalite(penalite);
    form.setFieldsValue({
      ...penalite,
      Nomcli: penalite.Nomcli || '',
    });
    if (penalite.PenaliteID != null) {
      loadDetail(penalite.PenaliteID);
    }
    setActiveTab('2');
  };

  // Chargement des détails
  const loadDetail = (penaliteId: number) => {
    setLoading(true);
    const searchParams: Params = {
      CodeAgence: '',
      Table: 'ViewAnyPenaliteDetail',
      Colonne1: 'PenaliteID',
      Colonne2: '',
      Init: penaliteId.toString(),
      Colagence: '',
      Choix: 35,
    };
    const config = {
      method: 'get',
      url: Data.ApiUrl.AnySearch,
      headers: {
        'Content-Type': 'application/json',
        Authorization: authHeader(),
        CypherSearch: Crypter.encrypt(JSON.stringify(searchParams)),
      },
    };
    axios
      .request(config)
      .then((response) => {
        setLoading(false);
        if (!response.data) return;
        const res = JSON.parse(Crypter.decrypt(response.data)).Value;
        if (res.StatutCode === 200) {
          const detail = res.Result?.Table?.[0] ?? null;
          if (!detail) {
            SweetAlert.fire({ title: 'Aucun détail trouvé', icon: 'warning' });
            return;
          }

          setDetailData(detail);
          form.setFieldsValue({
            PenaliteID: detail.PenaliteID,
            CodeFinancement: detail.CodeFinancement,
            NumeroFinancement: detail.NumeroFinancement,
            Nomcli: detail.Nomcli || detail.NomClient || '',
            NumeroEcheance: detail.NumeroEcheance,
            DateConstat: detail.DateConstat,
            JoursRetard: detail.JoursRetard,
            BaseCalcul: detail.BaseCalcul,
            TauxPenalite: detail.TauxPenalite,
            MontantPenalite: detail.MontantPenalite,
            MontantEncaisse: detail.MontantEncaisse,
            SoldePenalite: detail.SoldePenalite,
            CompteCreancePenalite: detail.CompteCreancePenalite,
            CompteCharite: detail.CompteCharite,
            CompteSourceClient: detail.CompteSourceClient,
            RefConstat: detail.RefConstat,
            RefEncaissement: detail.RefEncaissement,
            RefAnnulation: detail.RefAnnulation,
            Statut: detail.Statut,
            DateEncaissement: detail.DateEncaissement,
            MotifAnnulation: detail.MotifAnnulation,
          });
        } else {
          SweetAlert.fire({ title: res.Message, icon: 'warning' });
        }
      })
      .catch((error) => {
        setLoading(false);
        SweetAlert.fire({ title: 'Erreur', text: error?.message || String(error), icon: 'error' });
      });
  };

  // Sélection des lignes
  const rowSelection = {
    selectedRowKeys,
    checkStrictly: false,
    onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
  };

  const nombreSelection = selectedPenalites.length;

  // Onglets
  const items: TabsProps['items'] = [
    {
      key: '1',
      label: <span>Liste des pénalités</span>,
      children: (
        <Paper elevation={2} sx={{ p: 2, mt: 2, borderRadius: 2 }}>
          {/* En-tête */}
          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <Input
                placeholder="Rechercher..."
                allowClear
                size="large"
                style={{ width: '100%' }}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                prefix={<SearchOutlined />}
              />
            </Grid>
            <Grid item xs={12} md={6} display="flex" justifyContent="flex-end" gap={1}>
              <Button icon={<ReloadOutlined />} onClick={loadPenalites}>Rafraîchir</Button>
            </Grid>
          </Grid>

          {/* Statistiques avec formateur entier sans devise */}
          <Card size="small" style={{ marginBottom: 16 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Statistic title="Pénalités" value={totals.nombre} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Statistic
                  title="Montant constaté"
                  value={totals.montantConstate}
                  formatter={(v) => Number(v).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Statistic
                  title="Montant a imputer"
                  value={totals.montantImpute}
                  valueStyle={{ color: totals.ecart === 0 ? undefined : '#d48806' }}
                  formatter={(v) => Number(v).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Statistic
                  title="Écart"
                  value={totals.ecart}
                  valueStyle={{ color: totals.ecart === 0 ? '#389e0d' : '#d48806' }}
                  formatter={(v) => Number(v).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                />
              </Grid>
            </Grid>
          </Card>

          {/* Bouton demande */}
          <Box sx={{ mb: 2 }}>
            <Space wrap size="middle">
              <Button
                type="primary"
                icon={<CheckOutlined />}
                disabled={selectedPenalites.length === 0}
                onClick={handleDemande}
              >
                Demander l'imputation
              </Button>
              {nombreSelection > 0 && (
                <Tag icon={<UserOutlined />} color="blue" style={{ fontSize: 13, padding: '5px 10px' }}>
                  {nombreSelection} sélectionnée(s)
                </Tag>
              )}
            </Space>
          </Box>

          {/* Résumé sélection sans XAF et formatté en entier */}
          {nombreSelection > 0 && (
            <Card size="small" style={{ marginBottom: 16 }}>
              <Space wrap split={<Divider type="vertical" />}>
                <span><strong>Sélection :</strong> {nombreSelection} pénalité(s)</span>
                <span><strong>Constaté :</strong> {formatMontant(totalsSelection.constate)}</span>
                <span><strong>Imputé :</strong> {formatMontant(totalsSelection.impute)}</span>
                <span><strong>Écart :</strong> {formatMontant(totalsSelection.constate - totalsSelection.impute)}</span>
              </Space>
            </Card>
          )}

          {/* Tableau */}
          <Table
            size="small"
            bordered
            rowSelection={rowSelection}
            columns={columns}
            dataSource={groupedData}
            rowKey="key"
            scroll={{ x: 1450, y: 500 }}
            pagination={{
              pageSize: 50,
              showSizeChanger: true,
              pageSizeOptions: ['25', '50', '100', '200'],
              showTotal: (total, range) => `${range[0]}-${range[1]} sur ${total} pénalité(s)`,
            }}
            expandable={{ defaultExpandAllRows: false }}
            rowClassName={(record) => {
              if ('children' in record && Array.isArray(record.children)) return 'penalite-group-row';
              const penalite = record as PenaliteRow;
              return isMontantModifie(penalite) ? 'penalite-modified-row' : '';
            }}
          />
        </Paper>
      ),
    },
    {
      key: '2',
      label: <span>Détails de la pénalité</span>,
      children: (
        <Paper elevation={2} sx={{ p: 2, mt: 2, borderRadius: 2 }}>
          {selectedPenalite ? (
            <Tab1 form={form} detail={detailData} loading={loading} />
          ) : (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography color="text.secondary">Sélectionnez une pénalité pour afficher ses détails.</Typography>
            </Box>
          )}
        </Paper>
      ),
    },
  ];

  return (
    <div className="tab">
      <Spin spinning={loading} size="large" style={{ color: 'green' }}>
        <Tabs activeKey={activeTab} onChange={setActiveTab} size="large" type="card" items={items} />
      </Spin>
    </div>
  );
};

export default DemandePenalite;